import { useState, useEffect } from 'react';
import { MapLocation } from '../types/maps';
import { DEFAULT_LOCATION } from '../utils/geocoding';

interface UseCurrentLocationResult {
  location: MapLocation | null;
  loading: boolean;
  error: string | null;
  permissionDenied: boolean;
}

export function useCurrentLocation(): UseCurrentLocationResult {
  const [location, setLocation] = useState<MapLocation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);

  useEffect(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      setLoading(false);
      setLocation(DEFAULT_LOCATION);
      return;
    }

    const geoSuccess = (pos: GeolocationPosition) => {
      setLocation({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      });
      setLoading(false);
    };

    const geoError = (err: GeolocationPositionError) => {
      if (err.code === err.PERMISSION_DENIED) {
        setPermissionDenied(true);
        setError('We\'ll use your current location to make reporting easier. Please enable location permission for the most accurate location of the violation.');
      } else if (err.code === err.POSITION_UNAVAILABLE) {
        setError('Location information is unavailable. Showing default location.');
      } else if (err.code === err.TIMEOUT) {
        setError('Location request timed out. Showing default location.');
      } else {
        setError('An unknown error occurred. Showing default location.');
      }
      setLocation(DEFAULT_LOCATION);
      setLoading(false);
    };

    setLoading(true);
    navigator.geolocation.getCurrentPosition(geoSuccess, geoError, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    });
  }, []);

  return { location, loading, error, permissionDenied };
} 